import React from 'react';
import { Upload, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { cn } from '../../../../../lib/utils';
import { useDocumentUpload } from '../../hooks/useDocumentUpload';

interface DocumentUploaderProps {
  onUploadComplete?: () => void;
}

export const DocumentUploader: React.FC<DocumentUploaderProps> = ({ onUploadComplete }) => {
  const [error, setError] = React.useState<string | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const { handleUpload, acceptedTypes } = useDocumentUpload(onUploadComplete);

  const onDrop = async (acceptedFiles: File[]) => {
    setError(null);
    setIsUploading(true);
    const uploadError = await handleUpload(acceptedFiles);
    setIsUploading(false);
    if (uploadError) setError(uploadError);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedTypes,
    maxSize: 10 * 1024 * 1024,
  });

  return (
    <div className="space-y-6">
      <div
        {...getRootProps()}
        className={cn(
          "border-2 border-dashed rounded-lg p-8 text-center transition-colors",
          isUploading ? "cursor-not-allowed opacity-50" : "cursor-pointer",
          isDragActive ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20" : "border-gray-300 dark:border-gray-600",
          !isUploading && "hover:border-gray-400 dark:hover:border-gray-500"
        )}
      >
        <input {...getInputProps()} disabled={isUploading} />
        <div className="flex flex-col items-center">
          {isUploading ? (
            <>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mb-4"></div>
              <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
                Upload en cours...
              </p>
            </>
          ) : (
            <>
              <Upload className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-lg font-medium text-gray-700 dark:text-gray-300">
                {isDragActive ? 'Déposez vos documents ici' : 'Glissez-déposez vos documents ici'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                ou cliquez pour sélectionner des fichiers
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                PDF, Images, Word, Excel • 10MB max par fichier • 20 documents maximum
              </p>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 dark:bg-red-900/20">
          <div className="flex">
            <X className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                Erreur lors du téléchargement
              </h3>
              <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
